/**
 * Shared constants + helpers for the company edit-request banner.
 *
 * Kept as pure JS (no React / MUI imports) so sagas, selectors, and any
 * non-component caller can pull `mapBackendStatusToBanner` without paying for
 * the icon bundle that `EditRequestBanner.jsx` ships.
 */

export const EDIT_REQUEST_BANNER_STATUS = Object.freeze({
  APPROVED: "approved",
  DECLINED: "declined",
  PENDING: "pending",
  REVIEWING: "reviewing",
});

// Every BE alias we've seen so far for each banner status. Add new aliases
// here when the BE introduces one — the component/page layer doesn't need to
// know the specifics.
const BACKEND_STATUS_ALIASES = {
  [EDIT_REQUEST_BANNER_STATUS.APPROVED]: ["approved"],
  [EDIT_REQUEST_BANNER_STATUS.DECLINED]: ["rejected"],
  [EDIT_REQUEST_BANNER_STATUS.PENDING]: ["pending"],
  [EDIT_REQUEST_BANNER_STATUS.REVIEWING]: ["in_review"],
};

// Flatten aliases → banner status at module load so the runtime lookup is an
// O(1) object access instead of a switch walk per render.
const ALIAS_TO_BANNER_STATUS = Object.entries(BACKEND_STATUS_ALIASES).reduce(
  (acc, [bannerStatus, aliases]) => {
    aliases.forEach((alias) => {
      acc[alias] = bannerStatus;
    });
    return acc;
  },
  {},
);

/**
 * Translate whatever `status` string the BE hands us on a company edit
 * request into one of the four banner variants. Returns `null` for empty or
 * unknown values so callers can fall back to "don't render the banner".
 */
export const mapBackendStatusToBanner = (status) => {
  if (!status) return null;
  const normalized = String(status).toLowerCase();
  return ALIAS_TO_BANNER_STATUS[normalized] || null;
};
